import { t } from '@hefe/i18n';
import {
  BasketballPracticeSaveRequest,
  BasketballPracticesSlice,
  BasketballTeamsSlice,
  GymsSlice,
  ModalsSlice,
  userActiveBasketballCoachTeamId,
} from '@hefe/store';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import CustomForm, {
  FormField,
} from '../../../core/components/form/custom-form';

interface AddEditPracticeFormProps {
  practice?: BasketballPracticeSaveRequest;
}

export default function AddEditPracticeForm(props: AddEditPracticeFormProps) {
  const { practice } = props;

  const dispatch = useAppDispatch();

  const [refresh, doRefresh] = useState(false);
  const [gym, setGym] = useState(practice?.gym_id ? true : false);

  const gyms = useSelector(GymsSlice.gymsSelectSelector);
  const activeCoachTeamId = useSelector(userActiveBasketballCoachTeamId);
  const teams = useSelector(
    BasketballTeamsSlice.basketballTeamsSelectWithoutPendingSelector
  );

  useEffect(() => {
    doRefresh((prev) => !prev);
  }, [activeCoachTeamId]);

  const formKey = `add-edit-practice-form-${practice ? practice.id : 'new'}`;

  const onSubmit = (values: any, resetForm: any) => {
    const setLoading = ModalsSlice.modalsActions.setModalLoading;
    const close = ModalsSlice.modalsActions.closeModal;

    const gymValue: any = {};
    if (gym) {
      gymValue['gym_id'] = values.gym_id;
    } else {
      gymValue['location'] = values.location;
    }

    const submitData: BasketballPracticeSaveRequest = {
      ...(practice ? { id: practice.id } : {}),
      ...values,
      ...gymValue,
      date: dayjs(values.date).format('YYYY-MM-DD HH:mm'),
    };

    dispatch(setLoading(true));
    dispatch(BasketballPracticesSlice.saveBasketballPractice(submitData))
      .unwrap()
      .then(() => dispatch(close()))
      .catch(() => null)
      .finally(() => dispatch(setLoading(false)));
  };

  const gymField = (): FormField => {
    if (!gym) {
      return {
        name: 'location',
        label: t('location'),
        type: 'text',
        initalValue: practice?.location,
        rules: [
          {
            required: true,
            message: t('form_errors.name'),
          },
        ],
      };
    } else {
      return {
        name: 'gym_id',
        label: t('gyms.singular'),
        type: 'select',
        initalValue: practice?.gym_id,
        selectOptions: gyms,
        rules: [
          {
            required: true,
            message: t('form_errors.select_value'),
          },
        ],
      };
    }
  };

  const teamsField = (): FormField => {
    const selectOptions = teams.map((team) =>
      team.value === activeCoachTeamId ? { ...team, disabled: true } : team
    );

    let initialValue = [...(practice?.team_ids ?? [])];
    if (activeCoachTeamId) {
      initialValue.push(activeCoachTeamId);
    }

    initialValue = [...new Set(initialValue)];

    return {
      name: 'team_ids',
      label: t('teams'),
      type: 'select',
      selectOptions: selectOptions,
      multiSelect: true,
      initalValue: initialValue,
      disableSelectClear: true,
      rules: [],
    };
  };

  return (
    <CustomForm
      key={formKey}
      formId={formKey}
      refresh={refresh}
      fields={[
        {
          name: 'date',
          label: t('date'),
          type: 'datetime',
          initalValue: practice?.date,
          rules: [
            {
              required: true,
              message: t('form_errors.date'),
            },
          ],
        },
        { ...teamsField() },
        {
          name: 'in_gym',
          label: t('gyms.singular') + '?',
          type: 'checkbox',
          initalValue: practice?.gym_id !== null,
          value: gym,
          onChange: (value) => setGym(value),
          rules: [],
        },
        { ...gymField() },
        {
          name: 'description',
          label: t('note'),
          type: 'text',
          initalValue: practice?.description,
          rules: [],
        },
        {
          name: 'duration',
          label: `${t('duration')} (${t('in_min')})`,
          type: 'number',
          initalValue: practice?.duration,
          rules: [
            {
              required: true,
              message: t('form_errors.number'),
            },
            {
              type: 'number',
              max: 1440,
              message: t('form_errors.number_max', { max: 1440 }),
            },
            {
              type: 'number',
              min: 1,
              message: t('form_errors.number_min', { min: 1 }),
            },
          ],
        },
        {
          name: 'limit',
          label: t('limit'),
          type: 'number',
          initalValue: practice?.limit,
          rules: [],
        },
        {
          name: 'canceled',
          label: t('canceled'),
          type: 'checkbox',
          initalValue: practice?.canceled,
          rules: [],
        },
      ]}
      onSubmit={onSubmit}
    />
  );
}
